import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { LAUNCH_PROMO, isPromoLive, promoDismissKey } from '../promo';

/**
 * Temporary launch-promo bar, docked above the marketing nav on the homepage.
 *
 * ---------------------------------------------------------------------------
 * DESIGN NOTES
 * ---------------------------------------------------------------------------
 * Sits ABOVE the sticky nav in the DOM rather than being sticky itself, so it
 * scrolls away and the nav takes over the top edge. A permanently pinned bar
 * would eat viewport on every screen of a long landing page, and a promo is not
 * important enough to cost that on mobile.
 *
 * Brass field with ink text. The nav directly beneath it is heartwood at the top
 * of the page, so a heartwood bar would merge into it and read as one taller
 * masthead. Brass separates them and reads as a stamped notice, which suits the
 * archival register better than a coloured alert strip.
 *
 * Dismissible for the current browser session only. Closing it hides the bar for
 * the rest of that visit and it returns on the next one, which is the balance
 * between nagging someone who declined and a launch offer that a returning
 * visitor never sees again. `?promo=1` forces it back for checking the page.
 *
 * ---------------------------------------------------------------------------
 * ACCURACY
 * ---------------------------------------------------------------------------
 * The copy says "your first payment", not "your plan", because the Stripe coupon
 * is `duration: once`. It reads "any plan" rather than "anything" because
 * promotion codes are only accepted on subscription checkout, not on the one-off
 * packs. See `promo.ts` for the full constraints; do not loosen this wording
 * without checking the coupon.
 */
export function AnnouncementBar() {
  // `null` until storage has been read, so the bar never flashes in and
  // then vanishes for someone who already dismissed it.
  const [show, setShow] = useState<boolean | null>(null);

  useEffect(() => {
    // Desktop-only: the launch promo bar never shows on phones (founder request).
    // Mirrors the pre-paint promo gate in index.html, which also forces it off on
    // mobile so no `--promo-h` height is reserved.
    try {
      if (window.matchMedia('(max-width: 767px)').matches) {
        setShow(false);
        return;
      }
    } catch {
      /* matchMedia unavailable — fall through to the normal checks. */
    }
    if (!isPromoLive()) {
      setShow(false);
      return;
    }
    // `?promo=1` forces it back regardless of dismissal, so the page can always
    // be checked the way a first-time visitor sees it.
    if (new URLSearchParams(window.location.search).get('promo') === '1') {
      setShow(true);
      return;
    }
    try {
      // One-time cleanup. Dismissals were previously written to localStorage,
      // which is permanent — anyone who closed the bar before this change would
      // never see it again, which is exactly the behaviour being fixed. Clearing
      // the old key lets the session-scoped rule take over for them too.
      localStorage.removeItem(promoDismissKey());

      // sessionStorage: dismissal lasts for this browser tab only. See the note
      // on `promoDismissKey` for why this is not localStorage.
      setShow(sessionStorage.getItem(promoDismissKey()) !== '1');
    } catch {
      // Private browsing with storage blocked: show it rather than hide it.
      setShow(true);
    }
  }, []);

  if (!show) return null;

  const dismiss = () => {
    setShow(false);
    // Keep the pre-paint chrome offset honest: the bar just left the flow, so the
    // desktop hero (.lhero min-height uses var(--promo-h)) should reclaim its 54px
    // and re-center. The inline gate in index.html set these on load; this is the
    // one runtime transition it can't predict.
    document.documentElement.style.setProperty('--promo-h', '0px');
    document.documentElement.classList.remove('promo-on');
    try {
      sessionStorage.setItem(promoDismissKey(), '1');
    } catch {
      // Dismissal just will not persist. Not worth surfacing.
    }
  };

  return (
    <aside className="promobar" aria-label="Launch offer">
      <div className="promobar__inner">
        {/* The whole strip is the link. On a phone there is no room for a
            separate pill beside the copy, and stacking one underneath made the
            bar 103px tall — 12% of the viewport. So the pill shows only where it
            fits and the bar itself stays tappable everywhere. */}
        <Link href="/signup" className="promobar__hit">
        <p className="promobar__copy">
          <span className="promobar__eyebrow">Limited launch special</span>
          <span className="promobar__sep" aria-hidden="true">·</span>
          <span>
            {LAUNCH_PROMO.percentOff}% off your first payment
            {/* Only the plan qualifier is optional on a phone. "at checkout"
                stays at every width: it is the instruction telling someone WHERE
                to put the code, and a promo code with no destination is a puzzle
                rather than an offer. */}
            <span className="promobar__long"> on any plan</span> with code{' '}
            <strong className="promobar__code">{LAUNCH_PROMO.code}</strong>{' '}
            at checkout
          </span>
        </p>
          <span className="promobar__cta">Get Started Free</span>
        </Link>

        <button
          type="button"
          className="promobar__x"
          onClick={dismiss}
          aria-label="Dismiss this offer"
        >
          ✕
        </button>
      </div>

      <style>{`
        .promobar {
          background: var(--brass);
          color: var(--ink);
          /* No sticky: it scrolls away and the nav below takes the top edge. */
          position: relative;
          z-index: 101;
          user-select: none;
        }
        .promobar__inner {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 48px 0 14px;
          position: relative;
        }
        .promobar__hit {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
          padding: 9px 6px;
          text-decoration: none;
          color: inherit;
        }
        .promobar__copy {
          margin: 0;
          font-size: 13.5px;
          line-height: 1.45;
          color: var(--ink);
          text-align: center;
        }
        .promobar__eyebrow {
          font-family: var(--f-mono);
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.13em;
          text-transform: uppercase;
          /* Deep heartwood on brass, not white: white on gold is the generic
             sale-banner look this brand avoids. Uses the darker heartwood
             companion so the small mono kicker clears WCAG AA on brass
             (heartwood #1b3a2e was 3.78:1; heartwood-dark #102219 is 5.05:1)
             while staying in the dark-green family, not white-on-gold. */
          color: var(--heartwood-dark);
        }
        .promobar__sep { margin: 0 8px; color: rgba(15,23,20,0.4); }
        .promobar__code {
          font-family: var(--f-mono);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.06em;
          background: rgba(15,23,20,0.11);
          border-radius: 4px;
          padding: 2px 7px;
          white-space: nowrap;
        }
        .promobar__cta {
          flex: none;
          background: var(--heartwood);
          color: var(--parchment);
          font-size: 13px;
          font-weight: 600;
          padding: 8px 18px;
          border-radius: 999px;
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.15s ease;
        }
        .promobar__cta:hover { background: var(--heartwood-dark); }
        .promobar__x {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          /* 44px hit area, small glyph. The visual weight of a dismiss control
             should stay quiet, but the target has to be thumb-sized — it was
             24px, well under the 44px minimum. */
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          color: rgba(15,23,20,0.55);
          font-size: 13px;
          line-height: 1;
          cursor: pointer;
          border-radius: 50%;
          padding: 0;
          /* Above the full-strip link, or the link would swallow the tap. */
          z-index: 1;
        }
        .promobar__x:hover {
          background: rgba(15,23,20,0.10);
          color: var(--ink);
        }
        @media (max-width: 860px) {
          /* No button on a narrow screen — founder's call, and the right one.
             The button is the widest element in the bar, so dropping it lets the
             offer sit on one tight row instead of two, and a promo strip is not
             worth two rows of a phone viewport.
             Nothing is lost: the whole strip is already a link to /signup, so the
             action is still one tap. The faint underline is what signals that,
             since without a button there is no other affordance. */
          .promobar__cta { display: none; }
          .promobar__copy {
            text-decoration: underline;
            text-decoration-color: rgba(15, 23, 20, 0.3);
            text-underline-offset: 2px;
          }
        }
        @media (max-width: 720px) {
          .promobar__inner { padding: 0 44px 0 14px; }
          .promobar__hit { padding: 8px 2px; gap: 8px; }
          .promobar__copy { font-size: 12.5px; line-height: 1.45; }
          /* Only the eyebrow and the plan qualifier go. "at checkout" stays at
             every width: it is the instruction telling someone WHERE the code
             goes, and it was the one piece genuinely missing before. */
          .promobar__eyebrow,
          .promobar__sep,
          .promobar__long { display: none; }
          .promobar__code { font-size: 12px; padding: 1px 6px; }
        }
      `}</style>
    </aside>
  );
}
